import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const BASE_LABEL = { first: "1st", second: "2nd", third: "3rd", home: "Home" };
const NEXT_BASE = { first: "2nd", second: "3rd", third: "Home" };

export default function RundownModal({ rundownState, myRole, onSelectTarget, onFielderGuess, onRunnerGuess }) {
  const [selectedGuesses, setSelectedGuesses] = useState([]);
  const [selectedGuess, setSelectedGuess] = useState(null);
  const [lastResult, setLastResult] = useState(null); // most recent roll result
  const [resolved, setResolved] = useState(null);     // final outcome

  const { step, target_base, runner, occupied_bases, fielding_role, batting_role, round } = rundownState || {};
  const isFielder = myRole === fielding_role;
  const isRunner = myRole === batting_role;
  const runnerName = (runner || {}).name || "Runner";

  // Reset local selections when step advances
  useEffect(() => {
    setSelectedGuesses([]);
    setSelectedGuess(null);
  }, [step, round]);

  // Update last result when rundownState carries roll data
  useEffect(() => {
    if (rundownState?.roll_result) {
      setLastResult(rundownState.roll_result);
      if (rundownState.roll_result.outcome === "out" || rundownState.roll_result.outcome === "safe") {
        setResolved(rundownState.roll_result.outcome);
      }
    }
  }, [rundownState?.roll_result]);

  if (!rundownState) return null;

  const toggleGuess = (n) => {
    if (selectedGuesses.includes(n)) {
      setSelectedGuesses(selectedGuesses.filter(g => g !== n));
    } else if (selectedGuesses.length < 2) {
      setSelectedGuesses([...selectedGuesses, n]);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-slate-900 border border-slate-600 rounded-2xl p-5 w-full max-w-sm mx-4 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-black uppercase tracking-widest text-amber-400">Rundown</span>
          {target_base && (
            <span className="text-xs text-slate-400 font-mono">
              {runnerName} @ {BASE_LABEL[target_base]} · Round {round}
            </span>
          )}
        </div>

        {/* Last roll result — fixed-height slot so the modal doesn't resize each round */}
        <div className="min-h-[3.75rem] mb-3">
        <AnimatePresence mode="wait">
          {lastResult && !resolved && (
            <motion.div
              key={`result-${round}-${lastResult.actor}`}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="p-2 rounded-lg bg-slate-800 border border-slate-700 text-center"
            >
              {lastResult.actor === "fielder" ? (
                <span className="text-xs text-slate-300">
                  Fielder rolled <span className="font-mono font-black text-white">[{lastResult.dice?.join(", ")}]</span>
                  {" "}— guessed <span className="font-mono font-black text-amber-300">[{lastResult.guesses?.join(", ")}]</span>
                  {" "}— <span className="font-bold text-emerald-400">No match</span>
                </span>
              ) : (
                <span className="text-xs text-slate-300">
                  Runner rolled <span className="font-mono font-black text-white">{lastResult.die}</span>
                  {" "}— guessed <span className="font-mono font-black text-amber-300">{lastResult.guess}</span>
                  {" "}— <span className="font-bold text-emerald-400">No match</span>
                </span>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        </div>

        {/* Resolved */}
        {resolved && (
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className={`text-center py-3 rounded-xl font-black text-lg ${resolved === "out" ? "bg-rose-600 text-white" : "bg-emerald-600 text-white"}`}
          >
            {resolved === "out" ? `${runnerName} — OUT!` : `${runnerName} — SAFE!`}
          </motion.div>
        )}

        {/* Step: target_select */}
        {!resolved && step === "target_select" && (
          <div className="min-h-[10rem]">
            <p className="text-sm text-slate-300 mb-3 text-center">
              {isFielder ? "Choose a runner to target:" : "Fielder is selecting a runner…"}
            </p>
            {isFielder && (occupied_bases || []).map(base => (
              <button
                key={base}
                onClick={() => onSelectTarget(base)}
                className="w-full mb-2 py-2 px-4 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-300 font-bold hover:bg-amber-500/30 transition"
              >
                Runner on {BASE_LABEL[base]}
              </button>
            ))}
            {!isFielder && (
              <div className="text-center py-4 text-slate-500 text-sm animate-pulse">Waiting…</div>
            )}
          </div>
        )}

        {/* Step: fielder_guess */}
        {!resolved && step === "fielder_guess" && (
          <div className="min-h-[10rem]">
            <p className="text-sm text-slate-300 mb-3 text-center">
              {isFielder
                ? "Pick 2 numbers — then roll 2D6. Match = OUT."
                : `Fielder is choosing numbers… (Round ${round})`}
            </p>
            {isFielder && (
              <>
                <div className="flex justify-center gap-2 mb-4">
                  {[1, 2, 3, 4, 5, 6].map(n => (
                    <button
                      key={n}
                      onClick={() => toggleGuess(n)}
                      className={`w-10 h-10 rounded-lg font-black text-sm border-2 transition
                        ${selectedGuesses.includes(n)
                          ? "bg-red-500 border-red-400 text-white"
                          : "bg-slate-800 border-slate-600 text-slate-300 hover:border-red-500/60"}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <button
                  disabled={selectedGuesses.length !== 2}
                  onClick={() => onFielderGuess(selectedGuesses)}
                  className="w-full py-2 rounded-xl bg-red-600 text-white font-black text-sm disabled:opacity-40 hover:bg-red-500 transition"
                >
                  Roll 2D6 {selectedGuesses.length === 2 ? `(${selectedGuesses.join(", ")})` : ""}
                </button>
              </>
            )}
            {!isFielder && (
              <div className="text-center py-4 text-slate-500 text-sm animate-pulse">Fielder is rolling…</div>
            )}
          </div>
        )}

        {/* Step: runner_guess */}
        {!resolved && step === "runner_guess" && (
          <div className="min-h-[10rem]">
            <p className="text-sm text-slate-300 mb-3 text-center">
              {isRunner
                ? "Pick 1 number — then roll 1D6. Match = SAFE."
                : `${runnerName} is picking a number…`}
            </p>
            {isRunner && (
              <>
                <div className="flex justify-center gap-2 mb-4">
                  {[1, 2, 3, 4, 5, 6].map(n => (
                    <button
                      key={n}
                      onClick={() => setSelectedGuess(n)}
                      className={`w-10 h-10 rounded-lg font-black text-sm border-2 transition
                        ${selectedGuess === n
                          ? "bg-emerald-500 border-emerald-400 text-white"
                          : "bg-slate-800 border-slate-600 text-slate-300 hover:border-emerald-500/60"}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <button
                  disabled={selectedGuess === null}
                  onClick={() => onRunnerGuess(selectedGuess)}
                  className="w-full py-2 rounded-xl bg-emerald-600 text-white font-black text-sm disabled:opacity-40 hover:bg-emerald-500 transition"
                >
                  Roll 1D6 {selectedGuess !== null ? `(${selectedGuess})` : ""}
                </button>
              </>
            )}
            {!isRunner && (
              <div className="text-center py-4 text-slate-500 text-sm animate-pulse">Runner is rolling…</div>
            )}
          </div>
        )}

        {/* Force note */}
        {!resolved && target_base && target_base !== "first" && (
          <p className="mt-3 text-[10px] text-slate-600 text-center">
            Force play active — runners behind will advance if out.
          </p>
        )}
      </motion.div>
    </div>
  );
}
